// Lets plugins and the AI find and switch on plugins; plugins cannot reach the kernel (2026-10-07).

import { turnOnMany, turnOnWithNeeds } from './manager/needs.js'
import { START_SETS, groupOf, isEngine } from './manager/plugin-groups.js'

export function createPluginsService(kernel) {
  function list() {
    const st = kernel.getState()
    const { registry, installed } = st
    return registry
      .filter((m) => !isEngine(m.id))
      .map((m) => ({
        id: m.id,
        name: m.name ?? m.id,
        summary: m.summary || m.description || '',
        on: Boolean(installed[m.id]?.enabled),
        group: groupOf(m.id).name,
      }))
  }

  function find(query) {
    const q = String(query ?? '')
      .trim()
      .toLowerCase()
    if (!q) return list()
    return list().filter((p) => `${p.name} ${p.summary}`.toLowerCase().includes(q))
  }

  function resolve(idOrName) {
    const st = kernel.getState()
    const { registry } = st
    const searchStr = String(idOrName).trim().toLowerCase()

    const byId = registry.find((m) => m.id === idOrName)
    if (byId) return byId

    const byName = registry.find((m) => (m.name ?? m.id).toLowerCase() === searchStr)
    if (byName) return byName

    throw new Error(`No plugin named "${idOrName}"`)
  }

  async function turnOn(idOrName) {
    const st = kernel.getState()
    const { installed } = st
    const m = resolve(idOrName)

    if (installed[m.id]?.enabled) {
      return { on: m.name, also: [] }
    }

    const also = await turnOnWithNeeds(kernel, m.id)
    return { on: m.name, also }
  }

  function startSets() {
    const st = kernel.getState()
    const { registry } = st
    const registryById = new Map(registry.map((m) => [m.id, m]))

    return START_SETS.map((s) => ({
      id: s.id,
      name: s.name,
      blurb: s.blurb,
      plugins: s.ids.map((id) => registryById.get(id)?.name ?? id),
    }))
  }

  async function useStartSet(setId) {
    const searchStr = String(setId).trim().toLowerCase()
    const s = START_SETS.find((x) => x.id === setId || x.name.toLowerCase() === searchStr)

    if (!s) {
      throw new Error(`No start set "${setId}"`)
    }

    const on = await turnOnMany(kernel, s.ids)
    return { set: s.name, on }
  }

  // Enabled plugins that wait for first use: [{ id, name, contributes }].
  function dormant() {
    return (kernel.dormant?.() ?? []).map((m) => ({
      id: m.id,
      name: m.name ?? m.id,
      contributes: m.contributes ?? {},
    }))
  }

  // Start a dormant plugin now (its tool was clicked, its key pressed...). Resolves true when active.
  function wake(id) {
    return kernel.wake ? kernel.wake(id) : Promise.resolve(false)
  }

  // A plugin asks the kernel to wait before it starts the next plugin; returns { dispose }.
  function holdStarts(fn) {
    return kernel.holdStarts ? kernel.holdStarts(fn) : { dispose() {} }
  }

  // cb runs whenever plugin state changes; returns { dispose }.
  function subscribe(cb) {
    return kernel.subscribe(cb)
  }

  return {
    list,
    find,
    turnOn,
    startSets,
    useStartSet,
    dormant,
    wake,
    holdStarts,
    subscribe,
  }
}
